import type {
    RevalidateOutcome,
    RevalidateRequest,
    SecuritiesSnapshot,
    SecuritiesWorkerMessage,
    SecuritiesWorkerRequest,
    StoredSecurities,
} from './types'

export type SnapshotListener = (snapshot: SecuritiesSnapshot, validator: string | null) => void

/** One load cycle: read what is stored, then revalidate against the server. */
export interface SecuritiesSyncSession {
    loadStored(): Promise<StoredSecurities>
    revalidate(req: RevalidateRequest, onSnapshot: SnapshotListener): Promise<RevalidateOutcome>
    close(): void
}

async function createInlineSession(): Promise<SecuritiesSyncSession> {
    const { SecuritiesSyncer } = await import('./securitiesSync')
    const syncer = new SecuritiesSyncer()
    return {
        loadStored: () => syncer.loadStored(),
        revalidate: (req, onSnapshot) => syncer.revalidate(req, onSnapshot),
        close: () => {},
    }
}

function createWorkerSession(worker: Worker): SecuritiesSyncSession {
    type Pending = { resolve: (message: SecuritiesWorkerMessage) => void; reject: (error: Error) => void }
    let pending: Pending | null = null
    let onSnapshot: SnapshotListener | null = null
    let closed = false

    const fail = (error: Error) => {
        const current = pending
        pending = null
        current?.reject(error)
    }

    worker.onmessage = (event: MessageEvent<SecuritiesWorkerMessage>) => {
        const message = event.data
        if (message.type === 'snapshot') {
            onSnapshot?.(message.snapshot, message.validator)
            return
        }
        if (message.type === 'error') {
            fail(new Error(message.message))
            return
        }
        const current = pending
        pending = null
        current?.resolve(message)
    }
    worker.onerror = (event) => fail(new Error(event.message || 'securities worker failed'))

    const send = (request: SecuritiesWorkerRequest) =>
        new Promise<SecuritiesWorkerMessage>((resolve, reject) => {
            if (closed) {
                reject(new Error('securities worker session closed'))
                return
            }
            pending = { resolve, reject }
            worker.postMessage(request)
        })

    return {
        async loadStored() {
            const message = await send({ type: 'load' })
            if (message.type !== 'stored') throw new Error(`unexpected worker reply ${message.type}`)
            message.stored.timings.pageWait = Date.now() - message.postedAt
            return message.stored
        },
        async revalidate(req, listener) {
            onSnapshot = listener
            try {
                const message = await send({ type: 'revalidate', ...req })
                if (message.type !== 'revalidated') throw new Error(`unexpected worker reply ${message.type}`)
                message.outcome.timings.pageWait = Date.now() - message.postedAt
                return message.outcome
            } finally {
                onSnapshot = null
            }
        },
        close() {
            closed = true
            worker.terminate()
        },
    }
}

/**
 * Runs the IndexedDB I/O, fetch and protobuf decode in a Web Worker so the page thread only
 * pays for receiving the (much smaller) snapshot. Falls back to running inline when workers
 * are unavailable or fail to start.
 */
export async function createSecuritiesSyncSession(options: { inline?: boolean } = {}): Promise<SecuritiesSyncSession> {
    if (options.inline || typeof Worker === 'undefined') return createInlineSession()
    try {
        const worker = new Worker(new URL('./securities.worker.ts', import.meta.url), { type: 'module' })
        return createWorkerSession(worker)
    } catch {
        return createInlineSession()
    }
}
